import React from 'react';
import { Check } from '../../../icons';
import type { RosterRowRef } from '@discovr/features';
import { useCampaignCapabilitiesStore } from '@discovr/features';
import { isAddon, isMain, fmtDeliverableType } from '@discovr/types';
import type { DeliverableInstance } from '@discovr/types';
import { ActionConfirmDialog, CONFIRM_WORD } from '../../../components/common/ActionConfirmDialog';
import { formatMoneyExact } from '../../../lib/format';
import { rowDisplayName } from '../../utils/rowDisplayName';
import { resolveCreatorPlatformProfile } from '../../../lib/platformProfile';
import { DELIVERABLE_TYPE_META } from './deliverableTypeMeta';
import type { CreatorsRow } from './types';

/** Confirm-button label for the creator hard-delete. */
const DELETE_CONFIRM_LABEL = 'Delete creator';

export interface ConfirmAction {
  variant: 'approve-as-brand' | 'brand-approve' | 'admin-approve' | 'reject' | 'delete' | 'reinstate';
  creator: CreatorsRow;
}

interface ApprovalCardProps {
  confirmAction: ConfirmAction;
  confirmBusy: boolean;
  /** Full selection: main type + any add-ons. Empty means nothing pre-selected. */
  approveAsBrandDeliverables: string[];
  campaignPlatform: string | undefined;
  /**
   * True when the viewer is acting as an admin. Varies the reject-dialog copy
   * (admin reject → brand sees the reason; brand reject → it feeds future
   * creator recommendations).
   */
  isAdminViewer: boolean;
  onClose: () => void;
  onConfirm: (reason?: string, typedConfirm?: { confirm: string }) => Promise<void>;
  /** Replace the current selection with a new full array (main + add-ons). */
  onSetApproveAsBrandDeliverables: (types: string[]) => void;
  approveCreatorAsBrand?: ((creator: RosterRowRef, types: string[]) => Promise<void>) | null;
  rejectCreatorApproval?: ((creator: RosterRowRef, reason: string) => Promise<void>) | null;
}

export function ApprovalCard({
  confirmAction,
  confirmBusy,
  approveAsBrandDeliverables,
  campaignPlatform,
  isAdminViewer,
  onClose,
  onConfirm,
  onSetApproveAsBrandDeliverables,
}: ApprovalCardProps) {
  const platform = campaignPlatform ?? '';
  const variant = confirmAction.variant;
  const creator = confirmAction.creator;

  // Read-only members may see deliverables + prices but cannot change the
  // selection or confirm. Disables the pickers, no-ops the toggles, and
  // suppresses the confirm action below.
  const accessLevel = useCampaignCapabilitiesStore((s) => s.accessLevel);
  const isReadOnly = accessLevel === 'read';
  const creatorName = rowDisplayName(creator);
  // Show only the @username, clickable through to the profile — same pattern
  // as PlatformCreatorCell across the roster tabs.
  const { username, profileUrl, isVerified } = resolveCreatorPlatformProfile(
    creator as unknown as Record<string, unknown>,
    campaignPlatform,
  );

  // Fixed-word gate only for the (irreversible) delete; reject/approve have none.
  const deleteConfirmWord = variant === 'delete' ? CONFIRM_WORD : undefined;
  // ActionConfirmDialog's DEFAULT_LABELS maps 'delete' to the generic "Delete"
  // shared with object deletes (products, assignments); name the creator here.
  const deleteConfirmLabel = variant === 'delete' ? DELETE_CONFIRM_LABEL : undefined;

  const copy = (() => {
    switch (variant) {
      case 'approve-as-brand':
        return {
          title: 'Approve on behalf of brand?',
          description:
            "You're recording a brand approval for this creator. Pick the deliverable(s) the brand has agreed to — your selection is sent only when you confirm.",
        };
      case 'brand-approve':
        return {
          title: 'Approve Creator',
          description: 'Select the primary deliverable for this creator. This cannot be changed after approval.',
        };
      case 'admin-approve':
        return {
          title: 'Confirm admin approval?',
          description:
            "Review the deliverable(s) selected by the brand and confirm to finalize this creator.",
        };
      case 'reject':
        // Admin copy stays distinct: the reason is shown to the brand, not fed
        // into future-recommendation tuning.
        return {
          title: 'Reject creator',
          description: isAdminViewer
            ? 'This rejects the creator. Your reason, if provided, will be visible to the brand.'
            : 'Share your reasoning to help us improve your future creator recommendations.',
        };
      case 'delete':
        return {
          title: 'Delete creator permanently?',
          description:
            'This erases the creator from this campaign along with everything attached to them. They will NOT appear on the Rejected tab and cannot be sent back to Pending — re-import them to start over.',
        };
      case 'reinstate':
        return {
          title: 'Send back to Pending?',
          description:
            'This creator returns to the Pending tab for the brand to review again. Their prices and stats are kept; deliverables can be re-picked from there.',
        };
    }
  })();

  const extraContent =
    variant === 'approve-as-brand' || variant === 'brand-approve' || variant === 'admin-approve'
      ? (() => {
          const deliverables = creator.deliverables;
          if (deliverables.length === 0) {
            return (
              <div className="rounded-lg border border-dashed border-border bg-muted p-3 text-center text-[11px] text-muted-foreground">
                No deliverables on this entry. Use the edit pencil to add prices first.
              </div>
            );
          }

          // No price guard — the BE only sends deliverables priced >= 0, so a
          // ₹0 deliverable is a real option and must show.
          const mainDeliverables = deliverables.filter(
            (d) => isMain(String(d.type ?? ''), platform),
          );
          const addonDeliverables = deliverables.filter(
            (d) => isAddon(String(d.type ?? ''), platform),
          );

          const selectedMain = approveAsBrandDeliverables.find((t) => isMain(t, platform)) ?? '';
          const selectedAddons = approveAsBrandDeliverables.filter((t) => isAddon(t, platform));

          const handleMainToggle = (type: string) => {
            if (isReadOnly) return; // read-only: selection is frozen
            // Add-ons are retained when switching main type, so the user's
            // add-on intent survives a main change.
            const nextAddons = selectedAddons;
            onSetApproveAsBrandDeliverables(
              type === selectedMain ? [] : [type, ...nextAddons],
            );
          };

          const handleAddonToggle = (type: string) => {
            if (isReadOnly) return; // read-only: selection is frozen
            if (!selectedMain) return; // addons require a main
            const next = selectedAddons.includes(type)
              ? selectedAddons.filter((t) => t !== type)
              : [...selectedAddons, type];
            onSetApproveAsBrandDeliverables([selectedMain, ...next]);
          };

          return (
            <div className="space-y-3">
              {/* ── Deliverables (radio) — compact list rows ── */}
              <div className="space-y-1.5">
                <div className="text-xs font-medium uppercase text-muted-foreground">
                  Deliverables (select any one)
                </div>
                <div className="flex flex-col gap-1.5" role="radiogroup" aria-label="Deliverables">
                  {mainDeliverables.map((d) => {
                    const type = String(d.type ?? '');
                    const price = Number(d.base_price ?? 0);
                    const selected = selectedMain === type;
                    const meta = DELIVERABLE_TYPE_META[type];
                    const Glyph = meta?.Icon;
                    return (
                      <button
                        key={type}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => handleMainToggle(type)}
                        disabled={isReadOnly}
                        className={`flex h-[58px] items-center gap-3 rounded-lg px-3 text-left transition-colors ${
                          isReadOnly
                            ? `cursor-not-allowed opacity-50 ${selected ? 'border border-foreground bg-card' : 'border-[1.5px] border-border bg-card'}`
                            : selected
                              ? 'border border-foreground bg-card'
                              : 'border-[1.5px] border-border bg-card hover:border-foreground/30'
                        }`}
                      >
                        {Glyph && (
                          <span className="flex size-4 shrink-0 items-center justify-center text-foreground">
                            <Glyph size={16} weight="regular" />
                          </span>
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-foreground">
                            {meta?.label ?? fmtDeliverableType(type)}
                          </span>
                          {meta?.sub && (
                            <span className="block truncate text-xs text-muted-foreground">{meta.sub}</span>
                          )}
                        </span>
                        <span className="flex h-9 shrink-0 flex-col items-end justify-between">
                          <span
                            className={`flex size-3.5 items-center justify-center rounded-full ${
                              selected ? 'bg-(--brand-500) text-white' : 'invisible'
                            }`}
                            aria-hidden
                          >
                            {selected && <Check size={8} weight="bold" />}
                          </span>
                          {price > 0 && (
                            <span className="text-xs font-medium tabular-nums text-foreground">
                              {formatMoneyExact(price)}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Add-ons — pill checkboxes, disabled until a main is selected.
                  Usage rights shows here like any other add-on when priced. ── */}
              {addonDeliverables.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <div className="text-xs font-medium uppercase text-muted-foreground">Add-ons</div>
                    {!selectedMain && (
                      <span className="text-[10px] text-muted-foreground">(select a main first)</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {addonDeliverables.map((d) => {
                      const type = String(d.type ?? '');
                      const price = Number(d.base_price ?? 0);
                      const checked = selectedAddons.includes(type);
                      const disabled = !selectedMain || isReadOnly;
                      const days = (d as DeliverableInstance & { days?: number }).days;
                      return (
                        <button
                          key={type}
                          type="button"
                          role="checkbox"
                          aria-checked={checked}
                          disabled={disabled}
                          onClick={() => handleAddonToggle(type)}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-left transition-colors ${
                            disabled
                              ? 'cursor-not-allowed border-border bg-muted opacity-50'
                              : checked
                                ? 'border-foreground bg-card text-foreground'
                                : 'border-border bg-card text-foreground hover:border-foreground/30'
                          }`}
                        >
                          {checked && <Check size={13} weight="bold" className="shrink-0 text-foreground" />}
                          <span className="text-sm font-medium whitespace-nowrap">
                            {fmtDeliverableType(type)}
                            {type === 'usage_rights' && days != null && days > 0 && (
                              <span className="ml-1 font-medium text-muted-foreground">· {days}d</span>
                            )}
                            {price > 0 && (
                              <span className="ml-1 font-semibold tabular-nums">{formatMoneyExact(price)}</span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Selection summary ── */}
              {approveAsBrandDeliverables.length > 0 && (
                <div className="rounded-lg border border-border bg-muted px-3 py-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Will approve for
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {approveAsBrandDeliverables
                      .filter((t) => isMain(t, platform))
                      .map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center rounded-full bg-indigo-600 px-2.5 py-0.5 text-[11px] font-semibold text-white"
                        >
                          {fmtDeliverableType(t)}
                        </span>
                      ))}
                    {approveAsBrandDeliverables
                      .filter((t) => isAddon(t, platform))
                      .map((t) => {
                        const days = t === 'usage_rights'
                          ? (addonDeliverables.find((d) => d.type === t) as (DeliverableInstance & { days?: number }) | undefined)?.days
                          : undefined;
                        return (
                          <span
                            key={t}
                            className="inline-flex items-center rounded-full border border-border bg-card px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground"
                          >
                            + {fmtDeliverableType(t)}{days != null && days > 0 ? ` · ${days}d` : ''}
                          </span>
                        );
                      })}
                  </div>
                </div>
              )}

              <p className="text-[10px] font-medium text-muted-foreground">
                {isReadOnly
                  ? 'View-only — you do not have access to change or confirm this selection.'
                  : 'Selection is sent only on confirm. Cancel to discard.'}
              </p>
            </div>
          );
        })()
      : undefined;

  // At least one main deliverable must be selected to confirm. Read-only
  // members can never confirm — the deliverable picker is view-only.
  const canConfirm =
    isReadOnly
      ? false
      : variant === 'approve-as-brand' || variant === 'brand-approve' || variant === 'admin-approve'
        ? approveAsBrandDeliverables.some((t) => isMain(t, platform))
        : true;

  return (
    <ActionConfirmDialog
      open
      onOpenChange={(o) => !o && onClose()}
      variant={variant}
      title={copy.title}
      description={copy.description}
      creator={{
        name: creatorName,
        handle: username ? `@${username}` : undefined,
        profileUrl: profileUrl || undefined,
        isVerified,
      }}
      onConfirm={onConfirm}
      busy={confirmBusy}
      extraContent={extraContent}
      canConfirm={canConfirm}
      confirmText={deleteConfirmWord}
      confirmLabel={deleteConfirmLabel}
      {...(variant === 'reject' && !isAdminViewer
        ? {
            // Brand rejection requires a reason; admin's reject keeps it optional.
            reasonLabel: 'Reason for rejection',
            reasonMinLength: 1,
            reasonPlaceholder: "e.g., Budget mismatch, content style doesn't fit...",
          }
        : {})}
    />
  );
}
